import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ExternalLink, Gift, Check } from "lucide-react";
import axios from "axios";
import { fmtMoney, safeUrl, safeImg } from "@/lib/api";
import { cardCls } from "@/components/shared";

const Item = ({ item }) => (
  <li data-testid={`public-item-${item.id}`} className={`${cardCls} p-4 flex gap-4 items-center ${item.bought ? "opacity-60" : ""}`}>
    <div className="h-20 w-20 shrink-0 rounded-xl overflow-hidden bg-t-surface flex items-center justify-center">
      {item.image_url ? <img src={safeImg(item.image_url)} alt="" className="h-full w-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} /> : <Gift className="h-6 w-6 text-t-faint" />}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-medium">{item.name}</p>
      {item.notes && <p className="text-xs text-t-muted mt-0.5">{item.notes}</p>}
      {item.bought && <p className="inline-flex items-center gap-1 mt-1 text-[11px] font-medium text-t-c2-ink"><Check className="h-3 w-3" /> Already got this one</p>}
    </div>
    <div className="text-right shrink-0 space-y-1">
      {item.price != null && <p className="font-serif-display text-xl font-semibold">{fmtMoney(item.price)}</p>}
      {safeUrl(item.url) && (
        <a href={safeUrl(item.url)} target="_blank" rel="noreferrer" data-testid={`public-item-link-${item.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-t-primary hover:underline">
          View in shop <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  </li>
);

export default function PublicWishlist() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/wishlists/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError("This wishlist link isn't active any more. Ask for a new one."));
  }, [token]);

  return (
    <div className="min-h-screen relative z-10 px-5 sm:px-8 py-12" data-testid="public-wishlist-page">
      <div className="max-w-3xl mx-auto">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-t-primary mb-3">Base and Balance · Shared wishlist</p>
        {error && <p data-testid="public-wishlist-error" className="font-serif-display text-3xl">{error}</p>}
        {data && (
          <>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-none" data-testid="public-wishlist-title">{data.name}</h1>
            <p className="text-sm md:text-base text-t-muted mt-3 mb-10">
              {data.owner_name ? `${data.owner_name}'s wishlist` : "A wishlist"} · {data.items.length} {data.items.length === 1 ? "idea" : "ideas"}
            </p>
            <ul className="space-y-3">{data.items.map((i) => <Item key={i.id} item={i} />)}</ul>
            {!data.items.length && <p className="text-t-muted">Nothing on this wishlist yet.</p>}
          </>
        )}
      </div>
    </div>
  );
}
